import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { ArrowLeft, Send, Download, Upload, CheckCircle, AlertCircle, Package, Wrench, Truck, CheckSquare, Eye, X, Flag, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

const QuoteDetail = () => {
  const params = useParams();
  const quoteId = params.quoteId;
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showWorksiteDialog, setShowWorksiteDialog] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [locations, setLocations] = useState([]);
  const [remainingItems, setRemainingItems] = useState([]);
  const [completingWorksite, setCompletingWorksite] = useState(false);

  useEffect(() => {
    loadData();
  }, [quoteId]);

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}`);
      setQuote(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/send`);
      toast.success('Devis envoyé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur d\'envoi');
    }
  };

  const handleValidateManually = async () => {
    if (!window.confirm('Valider ce devis manuellement ? Les factures seront générées automatiquement.')) return;
    setValidating(true);
    try {
      await axios.post(`${API}/quotes/${quoteId}/validate`);
      toast.success('Devis validé - Factures générées');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de validation');
    } finally {
      setValidating(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devis_${quote.quote_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handlePreviewPDF = async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setShowPdfPreview(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'aperçu');
    }
  };

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleOpenWorksiteCompletion = async () => {
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}/materials`);
      setMaterials(res.data.materials || []);
      setLocations(res.data.locations || []);
      
      // Initialize remaining items with 0 quantity (nothing left by default)
      setRemainingItems(
        (res.data.materials || []).map(m => ({
          item_name: m.description,
          remaining_qty: 0,
          original_qty: m.quantity,
          unit: m.unit,
          unit_price: m.unit_price,
          return_to_location_id: res.data.locations?.[0]?.location_id || ''
        }))
      );
      
      setShowWorksiteDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des matériaux');
    }
  };

  const updateRemainingItem = (index, field, value) => {
    const newItems = [...remainingItems];
    newItems[index][field] = field === 'remaining_qty' ? parseFloat(value) || 0 : value;
    setRemainingItems(newItems);
  };

  const handleCompleteWorksite = async () => {
    // Filter items with remaining quantity > 0
    const itemsToReturn = remainingItems
      .filter(item => item.remaining_qty > 0)
      .map(item => ({
        item_name: item.item_name,
        remaining_qty: item.remaining_qty,
        unit: item.unit,
        unit_price: item.unit_price,
        return_to_location_id: item.return_to_location_id
      }));
    
    setCompletingWorksite(true);
    try {
      await axios.post(`${API}/worksite/complete`, {
        quote_id: quoteId,
        remaining_items: itemsToReturn,
        notes: `Fin de chantier - ${itemsToReturn.length} articles retournés au stock`
      });
      
      toast.success('Chantier terminé ! Matériaux restants retournés au stock.');
      setShowWorksiteDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la clôture du chantier');
    } finally {
      setCompletingWorksite(false);
    }
  };

  const handleUploadPDF = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/quotes/${quoteId}/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('PDF uploadé');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setUploading(false);
    }
  };

  const groupItemsByCategory = (items) => {
    const groups = {};
    items.forEach(item => {
      const cat = item.category || 'DIVERS';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  };

  const getCategoryStyle = (category) => {
    if (category === 'FOURNITURES') return { bg: 'bg-amber-100', text: 'text-amber-800', icon: <Package className="w-4 h-4" /> };
    if (category === "MAIN D'ŒUVRE") return { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Wrench className="w-4 h-4" /> };
    return { bg: 'bg-purple-100', text: 'text-purple-800', icon: <Truck className="w-4 h-4" /> };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!quote) return null;

  const groupedItems = quote.items ? groupItemsByCategory(quote.items) : {};
  const statusLabels = { draft: 'Brouillon', sent: 'Envoyé', accepted: 'Accepté' };
  const statusColors = { draft: 'bg-slate-100 text-slate-600', sent: 'bg-blue-100 text-blue-700', accepted: 'bg-emerald-100 text-emerald-700' };

  return (
    <div className="space-y-4 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/quotes')} className="w-9 h-9 p-0 flex-shrink-0">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">{quote.quote_number}</h1>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${statusColors[quote.status] || statusColors.draft}`}>
              {statusLabels[quote.status] || quote.status}
            </span>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <Card className="bg-white border border-slate-200 overflow-hidden">
        <div className="p-4">
          {/* Client */}
          <div className="mb-4 pb-4 border-b border-slate-100">
            <p className="text-sm text-slate-500">Client</p>
            <p className="font-semibold text-slate-900">{quote.client_name}</p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={handlePreviewPDF} variant="outline" size="sm" className="text-xs" data-testid="preview-pdf-btn">
              <Eye className="w-4 h-4 mr-1" /> Aperçu
            </Button>
            <Button onClick={handleDownloadPDF} variant="outline" size="sm" className="text-xs" data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            {quote.status === 'draft' && (
              <>
                <input type="file" accept=".pdf" onChange={handleUploadPDF} className="hidden" id="pdf-upload" />
                <Button onClick={() => document.getElementById('pdf-upload').click()} disabled={uploading} variant="outline" size="sm" className="text-xs">
                  <Upload className="w-4 h-4 mr-1" /> {uploading ? '...' : 'Upload'}
                </Button>
              </>
            )}
          </div>

          {quote.uploaded_pdf_filename && (
            <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-xs text-blue-700 truncate">{quote.uploaded_pdf_filename}</span>
            </div>
          )}

          {/* Description */}
          {quote.content && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Description</p>
              <p className="text-sm text-slate-700">{quote.content}</p>
            </div>
          )}

          {/* Items with colors */}
          {Object.keys(groupedItems).length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Détail</p>
              
              {Object.entries(groupedItems).map(([category, items]) => {
                const categoryTotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
                const style = getCategoryStyle(category);
                return (
                  <div key={category} className="mb-4">
                    {/* Category header with color */}
                    <div className={`flex justify-between items-center px-3 py-2 rounded-lg ${style.bg} ${style.text}`}>
                      <span className="font-semibold text-sm flex items-center gap-2">
                        {style.icon} {category}
                      </span>
                      <span className="font-mono text-sm font-bold">{categoryTotal.toFixed(2)} €</span>
                    </div>
                    
                    {/* Items */}
                    <div className="divide-y divide-slate-100 mt-1">
                      {items.map((item, idx) => (
                        <div key={idx} className="py-2 pl-2">
                          <div className="flex justify-between items-start gap-2">
                            <p className="text-sm text-slate-700 flex-1">{item.description}</p>
                            <p className="font-mono text-sm font-medium text-slate-900 flex-shrink-0">{(item.total || 0).toFixed(2)} €</p>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {item.quantity} {item.unit} × {(item.unit_price || 0).toFixed(2)} €
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Totals */}
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Total HT</span>
                <span className="font-mono font-medium">{quote.total_ht.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">TVA ({quote.tva}%)</span>
                <span className="font-mono font-medium">{(quote.total_ttc - quote.total_ht).toFixed(2)} €</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-slate-200">
                <span className="font-bold text-slate-900">Total TTC</span>
                <span className="font-mono font-bold text-lg text-primary">{quote.total_ttc.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          {/* Send Button */}
          {quote.status === 'draft' && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
              <Button className="w-full bg-primary text-white" onClick={handleSend}>
                <Send className="w-4 h-4 mr-2" /> Envoyer au client
              </Button>
              <Button 
                variant="outline" 
                className="w-full border-green-200 text-green-700 hover:bg-green-50" 
                onClick={handleValidateManually}
                disabled={validating}
              >
                <CheckSquare className="w-4 h-4 mr-2" /> 
                {validating ? 'Validation...' : 'Valider manuellement'}
              </Button>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Validation manuelle: génère les factures sans signature client
              </p>
            </div>
          )}

          {quote.status === 'sent' && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">En attente de signature client</p>
              </div>
              <Button 
                variant="outline" 
                className="w-full border-green-200 text-green-700 hover:bg-green-50" 
                onClick={handleValidateManually}
                disabled={validating}
              >
                <CheckSquare className="w-4 h-4 mr-2" /> 
                {validating ? 'Validation...' : 'Valider manuellement'}
              </Button>
            </div>
          )}

          {/* Worksite Completion for accepted quotes */}
          {quote.status === 'accepted' && quote.worksite_status !== 'completed' && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-700 font-medium">Devis accepté - Chantier en cours</p>
              </div>
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" 
                onClick={handleOpenWorksiteCompletion}
                data-testid="complete-worksite-btn"
              >
                <Flag className="w-4 h-4 mr-2" /> 
                Fin de chantier
              </Button>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Package className="w-3 h-3" /> Indiquez les matériaux restants pour les retourner au stock
              </p>
            </div>
          )}

          {quote.worksite_status === 'completed' && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Chantier terminé</p>
                  {quote.worksite_completed_at && (
                    <p className="text-xs text-slate-500">
                      Le {new Date(quote.worksite_completed_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Worksite Completion Dialog */}
      <Dialog open={showWorksiteDialog} onOpenChange={setShowWorksiteDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-emerald-600" />
              Fin de chantier - Matériaux restants
            </DialogTitle>
            <DialogDescription>
              Indiquez les quantités de matériaux qui n'ont pas été utilisés. Ils seront retournés au stock.
            </DialogDescription>
          </DialogHeader>
          
          {locations.length > 0 && remainingItems.length > 0 && (
            <div className="space-y-4">
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-medium">Matériau</th>
                      <th className="text-center p-3 font-medium w-24">Prévu</th>
                      <th className="text-center p-3 font-medium w-28">Restant</th>
                      <th className="text-left p-3 font-medium w-40">Retour vers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {remainingItems.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-3">
                          <span className="font-medium">{item.item_name}</span>
                        </td>
                        <td className="p-3 text-center text-slate-600">
                          {item.original_qty} {item.unit}
                        </td>
                        <td className="p-3">
                          <Input
                            type="number"
                            min="0"
                            max={item.original_qty}
                            value={item.remaining_qty}
                            onChange={(e) => updateRemainingItem(idx, 'remaining_qty', e.target.value)}
                            className="text-center h-8"
                          />
                        </td>
                        <td className="p-3">
                          <select
                            className="w-full p-1.5 border rounded text-sm"
                            value={item.return_to_location_id}
                            onChange={(e) => updateRemainingItem(idx, 'return_to_location_id', e.target.value)}
                          >
                            {locations.map(loc => (
                              <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <p className="font-medium mb-1">Résumé</p>
                <p>
                  {remainingItems.filter(i => i.remaining_qty > 0).length} article(s) seront retournés au stock
                </p>
              </div>
            </div>
          )}

          {materials.length === 0 && (
            <div className="p-4 text-center text-slate-500">
              Aucun matériau (fourniture) dans ce devis
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorksiteDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCompleteWorksite}
              disabled={completingWorksite}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-worksite-btn"
            >
              {completingWorksite ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Terminer le chantier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      {showPdfPreview && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closePdfPreview}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Aperçu du devis {quote.quote_number}</h3>
              <div className="flex items-center gap-2">
                <Button onClick={handleDownloadPDF} size="sm" variant="outline">
                  <Download className="w-4 h-4 mr-1" /> Télécharger
                </Button>
                <Button onClick={closePdfPreview} size="sm" variant="ghost" className="w-8 h-8 p-0">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 p-4 bg-slate-100 overflow-hidden">
              {pdfUrl && (
                <iframe 
                  src={pdfUrl} 
                  className="w-full h-full rounded-lg border border-slate-200 bg-white"
                  title="Aperçu PDF"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteDetail;
